import React, { useState } from "react";
import AppButton from "./AppButton";
import AppPeriodPicker from "./AppPeriodPicker";
import AppSelect from "./AppSelect";
import AppTooltip from "./AppTooltip";
import { IconChevronDown, IconChevronLeft, IconChevronRight } from "../icons";
import { formatPeriodTitle } from "../utils/periodDates";

/*
  Zeitraum-Navigation: einen Schritt zurück, die Beschriftung des Zeitraums als
  Auslöser der Auswahl, einen Schritt vor – und dort, wo eine Seite beide
  Ansichten führt, die Umschaltung Woche/Monat.

  Anders als AppNavigator, der durch eine bekannte Liste blättert, arbeitet
  diese Navigation auf einem Kalender: die Beschriftung entsteht aus dem Datum
  ("KW 32 · 03 - 09. Aug. 2026"), und die Auswahl ist ein Wochen- bzw.
  Monatsraster statt einer Liste.

  Die Schritte selbst führt die Seite aus (onPreviousPeriod/onNextPeriod): nur
  sie weiss, ob ihr Zustand ein Datum, ein Wochenstart oder ein Monat ist.
*/

export const PERIOD_VIEW_MODES = Object.freeze({ WEEK: "week", MONTH: "month" });

const WEEK_PERIOD_LABEL_PATTERN = /^(KW\s+\d+)\s*[\u00b7\u2022]\s*(.+)$/i;

function getWeekPeriodLabelParts(label) {
  const match = String(label || "").match(WEEK_PERIOD_LABEL_PATTERN);
  if (!match) return null;

  return {
    weekNumber: match[1],
    dateRange: match[2],
  };
}

export default function AppPeriodNavigator({
  viewMode = PERIOD_VIEW_MODES.WEEK,
  referenceDate,
  periodLabel = "",
  size = "md",
  previousLabel = "",
  nextLabel = "",
  weekLabel = "Woche",
  monthLabel = "Monat",
  viewModeLabel = "Zeitraumansicht",
  pickerLabels = null,
  disabled = false,
  className = "",
  onPreviousPeriod,
  onNextPeriod,
  onSelectDate,
  onViewModeChange,
}) {
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const isWeek = viewMode !== PERIOD_VIEW_MODES.MONTH;
  const normalizedViewMode = isWeek ? PERIOD_VIEW_MODES.WEEK : PERIOD_VIEW_MODES.MONTH;
  const label = periodLabel || formatPeriodTitle(normalizedViewMode, referenceDate);
  const weekLabelParts = isWeek ? getWeekPeriodLabelParts(label) : null;
  const previousText = previousLabel || (isWeek ? "Vorherige Woche" : "Vorheriger Monat");
  const nextText = nextLabel || (isWeek ? "Nächste Woche" : "Nächster Monat");

  const classes = [
    "ds-period",
    size === "sm" ? "ds-period--sm" : "",
    size === "md" ? "ds-period--md" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <AppTooltip content={previousText}>
        <AppButton
          variant="muted"
          size={size}
          iconOnly
          aria-label={previousText}
          disabled={disabled}
          onClick={() => onPreviousPeriod?.()}
        >
          <IconChevronLeft sx={{ fontSize: 18 }} />
        </AppButton>
      </AppTooltip>

      <button
        type="button"
        className="ds-period__trigger"
        aria-haspopup="dialog"
        aria-expanded={pickerAnchor ? "true" : "false"}
        aria-label={label}
        disabled={disabled}
        onClick={(event) => setPickerAnchor(event.currentTarget)}
      >
        <span className={`ds-period__value${weekLabelParts ? " ds-period__value--week" : ""}`}>
          {weekLabelParts ? (
            <>
              <span className="ds-period__week-number">{weekLabelParts.weekNumber}</span>
              <span className="ds-period__week-separator" aria-hidden="true">
                {"\u00b7"}
              </span>
              <span className="ds-period__date-range">{weekLabelParts.dateRange}</span>
            </>
          ) : label}
        </span>
        <IconChevronDown className="ds-period__icon" sx={{ fontSize: 18 }} />
      </button>

      <AppTooltip content={nextText}>
        <AppButton
          variant="muted"
          size={size}
          iconOnly
          aria-label={nextText}
          disabled={disabled}
          onClick={() => onNextPeriod?.()}
        >
          <IconChevronRight sx={{ fontSize: 18 }} />
        </AppButton>
      </AppTooltip>

      {/* Ohne onViewModeChange führt die Seite nur einen Zeitraum (z. B. eine Woche). */}
      {onViewModeChange ? (
        <AppSelect
          className="ds-period__view"
          size={size}
          value={normalizedViewMode}
          disabled={disabled}
          aria-label={viewModeLabel}
          options={[
            { value: PERIOD_VIEW_MODES.WEEK, label: weekLabel },
            { value: PERIOD_VIEW_MODES.MONTH, label: monthLabel },
          ]}
          onChange={(event) => onViewModeChange(event.target.value)}
        />
      ) : null}

      <AppPeriodPicker
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        viewMode={normalizedViewMode}
        referenceDate={referenceDate}
        onClose={() => setPickerAnchor(null)}
        onSelectDate={(date) => onSelectDate?.(date)}
        {...(pickerLabels || {})}
      />
    </div>
  );
}
